/**
 * §14.8 screen 5 — Fuqarolar (the appellants registry), list and detail.
 *
 * ===========================================================================
 * THIS IS NOT A CITIZEN DATABASE, AND THE SCREEN HAS TO SAY SO.
 * ===========================================================================
 *
 * §14.2.6: there is no citizen entity in this platform. Every row here is
 * derived on read from `murojaats` grouped by `phone_hmac` — nobody is
 * "registered", nothing is stored about a person who has not filed, and when
 * their appeals are deleted the row disappears with them. The banner at the top
 * of the list says exactly that, because a screen titled "Citizens" in a
 * government panel will otherwise be read as a population registry.
 *
 * Three rules the rendering exists to honour:
 *
 *  1. **No full phone number, anywhere on this screen.** Only the tail the API
 *     sends. The number stays behind click-to-reveal on the appeal detail,
 *     where unmasking it writes an audit row (`X-Reveal: phone`). A registry
 *     that printed it would retire that control silently.
 *  2. **A nameless appellant stays nameless.** `display_name` is absent for
 *     someone whose every appeal was a recording; they said their name aloud
 *     and we do not transcribe it into a registry field. Monogram plus em dash,
 *     never an invented name and never a photo placeholder (D-044).
 *  3. **A scoped count is LABELLED.** `scoped: true` means `appeal_count`
 *     counts only appeals inside this session's mahallas, so the column header
 *     changes rather than the number quietly meaning something else.
 *
 * Opening one person's file is an audited server call (`appellant.read`). That
 * is stated on the detail screen, in the same spirit as the phone-reveal note:
 * a staff member should know which of their reads are attributable.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { components } from '@navoiy/api-types';
import { ApiError, api, errorKey, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { AppellantMonogram } from '../components/AppellantMonogram.jsx';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { formatDate, formatDateTime } from '../lib/format.js';
import {
  Badge,
  Button,
  Card,
  DataRow,
  EmptyState,
  ErrorBanner,
  Spinner,
  Table,
  Td,
  Th,
  Tr,
  WarningBanner,
} from '../ui/index.jsx';

type Appellant = components['schemas']['AdminAppellant'];
type ListResponse = components['schemas']['AdminAppellantListResponse'];
type Detail = components['schemas']['AdminAppellantDetail'];

const PAGE_SIZE = 50;

export function AppellantsScreen(): React.JSX.Element {
  const { t, locale } = useI18n();
  const { expire } = useSession();

  const [items, setItems] = useState<Appellant[]>([]);
  const [scoped, setScoped] = useState(false);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (from: number) => {
      setLoading(true);
      setError(null);
      try {
        const data: ListResponse = unwrap(
          await api().GET('/admin/appellants', {
            params: { query: { limit: PAGE_SIZE, offset: from, locale } },
          }),
        );
        // Append on "load more", replace on a fresh read — so paging does not
        // lose the rows already on screen.
        setItems((previous) => (from === 0 ? data.items : [...previous, ...data.items]));
        setScoped(data.scoped);
        setNextOffset(data.next_offset ?? null);
      } catch (caught) {
        if (caught instanceof ApiError && caught.status === 401) {
          expire();
          return;
        }
        const { key, values } = errorKey(caught);
        setError(t(key, values));
      } finally {
        setLoading(false);
      }
    },
    [locale, expire, t],
  );

  useEffect(() => {
    void load(0);
  }, [load]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold text-brand-900">{t('admin.appellants.title')}</h1>
        {scoped ? <Badge tone="gold">{t('admin.scoped.label')}</Badge> : null}
      </div>

      {/* The screen's own honesty note — see the file header. */}
      <WarningBanner>{t('admin.appellants.derivedNotice')}</WarningBanner>

      <ErrorBanner message={error} />

      <Card title={t('admin.appellants.title')}>
        {loading && items.length === 0 ? <Spinner label={t('admin.common.loading')} /> : null}

        {!loading && items.length === 0 ? (
          <EmptyState message={t('admin.appellants.empty')} />
        ) : (
          <>
            <Table
              head={
                <tr>
                  <Th>{t('admin.appellants.name')}</Th>
                  <Th>{t('admin.appellants.phoneTail')}</Th>
                  <Th>{t('admin.column.mahalla')}</Th>
                  {/*
                    The column header itself carries the scope caveat: a scoped
                    session's `appeal_count` counts only in-scope appeals, and
                    the same header over two different meanings is the lie
                    §14.2.6 names.
                  */}
                  <Th>
                    {scoped
                      ? t('admin.appellants.appealCountScoped')
                      : t('admin.appellants.appealCount')}
                  </Th>
                  <Th>{t('admin.appellants.openCount')}</Th>
                  <Th>{t('admin.column.overdue')}</Th>
                  <Th>{t('admin.appellants.channels')}</Th>
                  <Th>{t('admin.appellants.firstAppeal')}</Th>
                  <Th>{t('admin.appellants.lastAppeal')}</Th>
                </tr>
              }
            >
              {items.map((person) => (
                <Tr key={person.appellant_id}>
                  <Td>
                    <Link
                      to={`/appellants/${person.appellant_id}`}
                      className="flex items-center gap-2 font-semibold text-brand-700 hover:underline"
                    >
                      <AppellantMonogram name={person.display_name} />
                      <span>{person.display_name ?? t('admin.appellants.noName')}</span>
                      {person.is_repeat ? (
                        <Badge tone="gold">{t('admin.appellants.repeat')}</Badge>
                      ) : null}
                    </Link>
                  </Td>
                  {/* The tail, and only the tail — the leading dots are the
                      panel's way of saying the rest exists and is not shown. */}
                  <Td className="tabular-nums">···{person.phone_tail}</Td>
                  <Td>{person.mahalla_name ?? '—'}</Td>
                  <Td className="tabular-nums font-semibold text-ink-900">
                    {person.appeal_count}
                  </Td>
                  <Td className="tabular-nums">{person.open_count}</Td>
                  <Td className="tabular-nums">
                    {person.overdue_count > 0 ? (
                      <span className="font-semibold text-danger-500">{person.overdue_count}</span>
                    ) : (
                      person.overdue_count
                    )}
                  </Td>
                  <Td>
                    <span className="flex flex-wrap gap-1">
                      {person.channels.map((channel) => (
                        <Badge key={channel}>{t(`admin.channel.${channel}`)}</Badge>
                      ))}
                    </span>
                  </Td>
                  <Td className="whitespace-nowrap">{formatDate(person.first_appeal_at)}</Td>
                  <Td className="whitespace-nowrap">{formatDateTime(person.last_appeal_at)}</Td>
                </Tr>
              ))}
            </Table>

            {nextOffset !== null ? (
              <div className="flex items-center gap-3 pt-4">
                <Button tone="secondary" disabled={loading} onClick={() => void load(nextOffset)}>
                  {t('admin.inbox.loadMore')}
                </Button>
                <span className="text-xs text-ink-500">
                  {t('admin.appellants.shown', { count: items.length })}
                </span>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail — `appellant.read`, and reading it writes an audit row
// ---------------------------------------------------------------------------

export function AppellantDetailScreen(): React.JSX.Element {
  const { appellantId = '' } = useParams();
  const { t, locale } = useI18n();
  const { expire } = useSession();

  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const data = unwrap(
          await api().GET('/admin/appellants/{appellant_id}', {
            params: { path: { appellant_id: appellantId }, query: { locale } },
          }),
        );
        if (!cancelled) setDetail(data);
      } catch (caught) {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 401) {
          expire();
          return;
        }
        const { key, values } = errorKey(caught);
        setError(t(key, values));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appellantId, locale, expire, t]);

  if (loading && !detail) return <Spinner label={t('admin.common.loading')} />;
  if (!detail) return <ErrorBanner message={error ?? t('admin.error.notFound')} />;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/appellants" className="text-sm font-semibold text-brand-700 hover:underline">
          ← {t('admin.common.back')}
        </Link>
        <AppellantMonogram name={detail.display_name} large />
        <h1 className="text-xl font-bold text-brand-900">
          {detail.display_name ?? t('admin.appellants.noName')}
        </h1>
        {detail.is_repeat ? <Badge tone="gold">{t('admin.appellants.repeat')}</Badge> : null}
        {detail.scoped ? <Badge tone="gold">{t('admin.scoped.label')}</Badge> : null}
      </div>

      <ErrorBanner message={error} />

      {/*
        The read the staff member has just performed is audited, and they are
        told so — the same courtesy the phone-reveal control extends on the
        appeal detail. §14.2.6 makes this the read that must be attributable;
        hiding that fact from the person performing it serves nobody.
      */}
      <WarningBanner>{t('admin.appellants.auditNotice')}</WarningBanner>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[2fr_3fr]">
        <Card title={t('admin.appellants.summaryTitle')}>
          <dl className="flex flex-col">
            <DataRow label={t('admin.appellants.phoneTail')}>
              <span className="tabular-nums">···{detail.phone_tail}</span>
            </DataRow>
            {/* Says why the full number is not here — otherwise its absence
                reads as a missing field rather than as a control. */}
            <DataRow label={t('admin.appellants.phoneFull')}>
              <span className="text-ink-500">{t('admin.appellants.phoneOnAppeal')}</span>
            </DataRow>
            <DataRow label={t('admin.column.mahalla')}>{detail.mahalla_name ?? '—'}</DataRow>
            <DataRow
              label={
                detail.scoped
                  ? t('admin.appellants.appealCountScoped')
                  : t('admin.appellants.appealCount')
              }
            >
              {detail.appeal_count}
            </DataRow>
            <DataRow label={t('admin.appellants.openCount')}>{detail.open_count}</DataRow>
            <DataRow label={t('admin.column.overdue')}>{detail.overdue_count}</DataRow>
            <DataRow label={t('admin.appellants.channels')}>
              <span className="flex flex-wrap gap-1">
                {detail.channels.map((channel) => (
                  <Badge key={channel}>{t(`admin.channel.${channel}`)}</Badge>
                ))}
              </span>
            </DataRow>
            <DataRow label={t('admin.appellants.firstAppeal')}>
              {formatDateTime(detail.first_appeal_at)}
            </DataRow>
            <DataRow label={t('admin.appellants.lastAppeal')}>
              {formatDateTime(detail.last_appeal_at)}
            </DataRow>
          </dl>
        </Card>

        <Card title={t('admin.appellants.appealsTitle')}>
          {detail.appeals.length === 0 ? (
            <EmptyState message={t('admin.appellants.empty')} />
          ) : (
            <Table
              head={
                <tr>
                  <Th>{t('admin.column.reference')}</Th>
                  <Th>{t('admin.column.received')}</Th>
                  <Th>{t('admin.column.status')}</Th>
                  <Th>{t('admin.column.legalType')}</Th>
                  <Th>{t('admin.column.kind')}</Th>
                  <Th>{t('admin.column.channel')}</Th>
                  <Th>{t('admin.column.mahalla')}</Th>
                </tr>
              }
            >
              {detail.appeals.map((appeal) => (
                <Tr key={appeal.id}>
                  <Td>
                    <Link
                      to={`/murojaats/${appeal.id}`}
                      className="font-semibold tracking-ref text-brand-700 hover:underline"
                    >
                      {appeal.reference}
                    </Link>
                  </Td>
                  <Td className="whitespace-nowrap">{formatDateTime(appeal.received_at)}</Td>
                  <Td>
                    <Badge tone={appeal.is_overdue ? 'danger' : 'neutral'}>
                      {t(`shared.murojaat.status.${appeal.status}`)}
                    </Badge>
                  </Td>
                  <Td>
                    {appeal.legal_type ? (
                      t(`admin.legalType.${appeal.legal_type}`)
                    ) : (
                      <span className="text-ink-400">
                        {t('admin.legalType.unclassified')}
                      </span>
                    )}
                  </Td>
                  <Td>{t(`shared.murojaat.kind.${appeal.kind}`)}</Td>
                  <Td>{t(`admin.channel.${appeal.channel}`)}</Td>
                  <Td>{appeal.mahalla_name}</Td>
                </Tr>
              ))}
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
}
